import { Component, OnInit } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { FeatureName } from '@app/core/interfaces/c5/feature-name';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { SecurityPermissionName } from '@app/core/models/permission';
import { License } from '@app/shared/models/license';
import { EndUserContextService } from '@app/shared/services/context/end-user-context.service';
import { EnterpriseAuthorizationService } from '@app/shared/services/enterprise-authorization.service';
import { hasEnterpriseLicense } from '@app/utils/has-enterprise-license';
import { Authorization } from '@business/end-user/models/authorization';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'portal-end-user-main-nav',
  templateUrl: './end-user-main-nav.component.html',
  styles: [],
  standalone: false,
})
export class EndUserMainNavComponent implements OnInit {
  public hasAcd: boolean = false;
  public hasCallCenter: boolean = false;
  public hasConferenceLicense: boolean = false;
  public hasDevice: boolean = false;
  public canUseRichPresence: boolean = false;
  public hasMs365Presence: boolean = false;

  public isCallCenterSupervisor: boolean = false;
  public isEndUser: boolean = false;

  public loading: boolean = true;

  Permission = SecurityPermissionName;

  constructor(
    private endUserContextService: EndUserContextService,
    private enterpriseAuthorizationService: EnterpriseAuthorizationService,
    private authenticationService: AuthenticationService
  ) {}

  public ngOnInit() {
    const canViewPresenceProfiles = this.authenticationService.hasPermission(
      SecurityPermissionName.VIEW_PRESENCE_PROFILE
    );
    combineLatest([
      this.endUserContextService.callCenter.data$,
      this.endUserContextService.endUser.data$,
      this.endUserContextService.enterprise$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([callCenter, endUser, enterprise]) => {
        this.hasAcd = endUser.isAcdQueueAgent ?? false;
        this.hasCallCenter = endUser.isCallCenterAgent ?? false;
        this.hasConferenceLicense = hasEnterpriseLicense(endUser.domain, License.Conference);
        this.hasDevice = endUser.user?.auth === Authorization.FULL;
        this.isCallCenterSupervisor = callCenter?.isSupervisor === true;

        if (canViewPresenceProfiles) {
          this.endUserContextService
            .canUseRichPresence$()
            .pipe(untilDestroyed(this))
            .subscribe((canUseRichPresence) => {
              // besides the conditions where the enterprise has active RichPresence service and end-user has
              // rich-presence feature, we also need to check if the presence profile mechanism is active
              // and if not if the user has the permission to do so. refs #70109

              const presenceProfilesActiveOrHavingPermissionToActivate =
                endUser.localData?.user.presenceProfileActive === true ||
                this.authenticationService.hasPermission(
                  SecurityPermissionName.ACTIVATE_PRESENCE_PROFILE_MECHANISM
                );

              this.canUseRichPresence =
                canUseRichPresence && presenceProfilesActiveOrHavingPermissionToActivate;
            });
        }

        this.loading = false;
      });

    combineLatest([
      this.endUserContextService.endUser.data$,
      this.enterpriseAuthorizationService.hasEnterpriseOption$(
        EnterpriseOptionType.Microsoft365PresenceSyncFeatures
      ),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([endUser, hasEnterpriseOption]) => {
        this.hasMs365Presence =
          this.authenticationService.hasPermission(SecurityPermissionName.VIEW_MS365_PRESENCE) &&
          hasEnterpriseOption &&
          endUser.user?.features?.some((feature) => feature.featName === FeatureName.MS_365_PRESENCE);
      });
  }
}
